import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Plus, Save, Trash2, Loader2, Search, ExternalLink, User, MessageCircle } from "lucide-react";
import { toast } from "sonner";
import { adminApi } from "../../lib/api";
import { openWhatsApp } from "../../lib/whatsapp";

// ---------- LIST ---------------------------------------------------
export const AdminBrokers = () => {
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const data = await adminApi.listBrokers(q ? { query: q } : {});
      setRows(Array.isArray(data) ? data : []);
    } catch { toast.error("Could not load brokers"); }
    finally { setLoading(false); }
  };
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); /* eslint-disable-next-line */ }, [q]);

  return (
    <div data-testid="admin-brokers">
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <div className="font-display uppercase tracking-widest text-xs text-[#f28a16]">Broker directory</div>
          <h1 className="font-display font-black uppercase text-3xl sm:text-4xl text-[#111619] mt-1">Brokers</h1>
          <p className="text-[#3f4b52] mt-1 text-sm max-w-2xl">
            Optional intermediaries you can attach to a job or invoice — reference only, no billing.
          </p>
        </div>
        <Link
          to="/admin/brokers/new"
          data-testid="new-broker-btn"
          className="bg-[#f28a16] text-white font-display uppercase tracking-widest text-xs px-5 h-11 rounded-md inline-flex items-center gap-2 hover:brightness-110"
        >
          <Plus size={14} /> New broker
        </Link>
      </div>

      <div className="mt-6">
        <div className="relative max-w-md">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#9aa6ad]" />
          <input
            value={q} onChange={(e) => setQ(e.target.value)}
            placeholder="Search brokerage / contact / email…"
            className="w-full pl-9 pr-3 h-10 border border-[#cfd7db] rounded-md text-sm"
            data-testid="brokers-search"
          />
        </div>
      </div>

      <div className="mt-6 bg-white border border-[#cfd7db] rounded-md overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-[#111619] text-white text-left">
            <tr>
              <Th>Brokerage</Th><Th>Contact person</Th><Th>Phone</Th><Th>Email</Th><Th>Status</Th><Th></Th>
            </tr>
          </thead>
          <tbody className="divide-y divide-[#eef2f4]">
            {loading && <tr><td colSpan={6} className="p-6 text-center text-[#3f4b52]">Loading…</td></tr>}
            {!loading && rows.length === 0 && (
              <tr><td colSpan={6} className="p-8 text-center text-[#3f4b52]" data-testid="brokers-empty">
                No brokers yet — click <b>New broker</b> to add one.
              </td></tr>
            )}
            {rows.map((r) => (
              <tr key={r.id} className="hover:bg-[#f5f6f6]" data-testid={`broker-row-${r.id}`}>
                <Td className="font-bold text-[#111619]">{r.name}</Td>
                <Td>{r.contact_person || "—"}</Td>
                <Td>{r.phone || "—"}</Td>
                <Td>{r.email || "—"}</Td>
                <Td>
                  <span className={`inline-block border px-2 py-0.5 rounded-sm text-xs font-display uppercase tracking-wider ${r.active ? "bg-[#ecfdf5] text-[#065f46] border-[#10b981]" : "bg-[#f5f6f6] text-[#3f4b52] border-[#cfd7db]"}`}>
                    {r.active ? "Active" : "Inactive"}
                  </span>
                </Td>
                <Td className="text-right">
                  {r.phone && (
                    <button
                      type="button"
                      onClick={(e) => {
                        e.preventDefault();
                        openWhatsApp(r.phone, `Hi ${(r.contact_person || "").split(" ")[0] || "there"}, this is Diesel & Dust Windscreens — `);
                      }}
                      className="text-[#25D366] hover:brightness-110 inline-flex items-center gap-1 text-xs mr-3"
                      data-testid={`whatsapp-broker-${r.id}`}
                      title="Open WhatsApp with this broker contact"
                    >
                      <MessageCircle size={13} /> WhatsApp
                    </button>
                  )}
                  <Link to={`/admin/brokers/${r.id}`} className="text-[#f28a16] hover:underline inline-flex items-center gap-1 text-xs" data-testid={`open-broker-${r.id}`}>
                    Open <ExternalLink size={11} />
                  </Link>
                </Td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// ---------- CREATE / EDIT -----------------------------------------
const blank = { name: "", contact_person: "", phone: "", email: "", notes: "", active: true };

export const AdminBrokerForm = () => {
  const { id } = useParams();
  const nav = useNavigate();
  const editing = !!id;
  const [form, setForm] = useState(blank);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!editing) return;
    (async () => {
      try { setForm({ ...blank, ...(await adminApi.getBroker(id)) }); }
      catch { toast.error("Could not load broker"); }
    })();
  }, [id, editing]);

  const set = (k) => (e) => setForm({ ...form, [k]: e?.target?.type === "checkbox" ? e.target.checked : (e?.target?.value ?? e) });

  const save = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return toast.error("Brokerage name is required");
    setBusy(true);
    try {
      const payload = { ...form, email: form.email || null };
      const saved = editing ? await adminApi.updateBroker(id, payload) : await adminApi.createBroker(payload);
      toast.success(editing ? "Broker updated" : "Broker created");
      nav(`/admin/brokers/${saved.id}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not save broker");
    } finally { setBusy(false); }
  };

  const removeBroker = async () => {
    if (!window.confirm("Delete this broker?")) return;
    setBusy(true);
    try {
      await adminApi.deleteBroker(id);
      toast.success("Broker deleted");
      nav("/admin/brokers");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not delete");
    } finally { setBusy(false); }
  };

  return (
    <form onSubmit={save} data-testid="admin-broker-form">
      <Link to="/admin/brokers" className="inline-flex items-center gap-2 text-xs font-display uppercase tracking-widest text-[#3f4b52] hover:text-[#f28a16]">
        <ArrowLeft size={14} /> Back to brokers
      </Link>
      <div className="mt-3 flex items-start justify-between flex-wrap gap-4">
        <div>
          <div className="font-display uppercase tracking-widest text-xs text-[#f28a16]">Broker</div>
          <h1 className="font-display font-black uppercase text-3xl sm:text-4xl text-[#111619] mt-1">
            {editing ? form.name || "Edit broker" : "New broker"}
          </h1>
        </div>
        <div className="flex gap-2">
          {editing && (
            <button type="button" onClick={removeBroker} disabled={busy} data-testid="broker-delete-btn"
              className="border border-[#dc2626] text-[#dc2626] font-display uppercase tracking-widest text-xs px-4 h-10 rounded-md inline-flex items-center gap-2 hover:bg-[#fef2f2]">
              <Trash2 size={13} /> Delete
            </button>
          )}
          <button type="submit" disabled={busy} data-testid="broker-save-btn"
            className="bg-[#f28a16] text-white font-display uppercase tracking-widest text-xs px-5 h-10 rounded-md inline-flex items-center gap-2 hover:brightness-110">
            {busy ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
            {editing ? "Save changes" : "Create broker"}
          </button>
        </div>
      </div>

      <div className="mt-6 bg-white border border-[#cfd7db] rounded-md p-5 grid grid-cols-1 md:grid-cols-2 gap-4 max-w-4xl">
        <F label="Brokerage name *"><Inp val={form.name} on={set("name")} testid="broker-name" /></F>
        <F label="Contact person"><Inp val={form.contact_person} on={set("contact_person")} testid="broker-contact-person" /></F>
        <F label="Phone"><Inp val={form.phone} on={set("phone")} testid="broker-phone" /></F>
        <F label="Email"><Inp type="email" val={form.email} on={set("email")} testid="broker-email" /></F>
        <F label="Notes" span={2}><textarea value={form.notes} onChange={set("notes")} rows={3} className="w-full px-3 py-2 border border-[#cfd7db] rounded-md text-sm" /></F>
        <F label="Status">
          <label className="inline-flex items-center gap-2 text-sm text-[#3f4b52] h-10">
            <input type="checkbox" checked={form.active} onChange={set("active")} className="h-4 w-4 accent-[#f28a16]" data-testid="broker-active" />
            {form.active ? "Active" : "Inactive"}
          </label>
        </F>
      </div>
    </form>
  );
};

// ---------- shared bits -------------------------------------------
const Th = ({ children }) => <th className="px-3 py-2 font-display uppercase tracking-widest text-xs">{children}</th>;
const Td = ({ children, className = "" }) => <td className={`px-3 py-2.5 ${className}`}>{children}</td>;
const F = ({ label, children, span = 1 }) => (
  <div className={span === 2 ? "md:col-span-2" : ""}>
    <label className="text-xs font-display uppercase tracking-wider font-bold text-[#3f4b52] block mb-1">{label}</label>
    {children}
  </div>
);
const Inp = ({ val, on, type = "text", testid }) => (
  <input type={type} value={val ?? ""} onChange={on} data-testid={testid}
    className="w-full h-10 px-3 border border-[#cfd7db] rounded-md text-sm" />
);

export default AdminBrokers;
